import { useOpenModal } from '@flowus/common/next-modal';
import { PayType } from '@flowus/common/utils/pay-info';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Button } from 'src/common/components/button';
import { Icon } from 'src/common/components/icon';
import { ListView } from 'src/common/components/list-view';
import { ListItemType } from 'src/common/components/list-view/types';
import { message } from 'src/common/components/message';
import { request } from 'src/common/request';
import { Modals } from 'src/modals';
import { PayQrModal } from '../select-upgrade-plan/pay-qr-modal';
import { getCurrencySymbols } from '@flowus/common';

/** 购买数量选项 */
const COUNT_OPTIONS = [100, 200, 500, 1000, 2000, 5000];

export interface BuyWelfareCodeModalProps {
  uuid: string;
  spaceId: string;
  accessFee: number;
  onClose: () => void;
  onSuccess?: () => void;
}

/** 购买兑换码弹窗 */
export const BuyWelfareCodeModal: FC<BuyWelfareCodeModalProps> = (props) => {
  const { uuid, spaceId, accessFee, onClose, onSuccess } = props;
  const openModal = useOpenModal();
  const [count, setCount] = useState(100);
  const [loading, setLoading] = useState(false);

  // 兑换码单价 = 付费金额 × 10%
  const unitPrice = useMemo(() => {
    return Number((accessFee * 0.1).toFixed(2));
  }, [accessFee]);

  // 总金额
  const totalAmount = useMemo(() => {
    return Number((unitPrice * count).toFixed(2));
  }, [unitPrice, count]);

  // 数量选择下拉数据
  const countOptions = COUNT_OPTIONS.map((value) => ({
    type: ListItemType.TEXT_DESCRIPTION,
    data: {
      title: `${value}`,
      selected: value === count,
      onClick: () => setCount(value),
    },
  }));

  // 创建订单
  const createOrder = async (payType: PayType = PayType.wxpay) => {
    const ret = await request.infra.createSharePaidWelfareCodeOrder({
      spaceId,
      sharePageId: uuid,
      payType,
      codeCount: count,
    });
    return ret;
  };

  // 支付成功后回调
  const handlePaySuccess = () => {
    openModal.warning({
      title: '兑换码购买成功',
      content: `共生成 ${count} 个兑换码，可在「我的兑换码」中查看。`,
      confirmText: '知道了',
      colorType: 'active',
      confirm: () => {
        onSuccess?.();
        onClose();
      },
    });
  };

  // 去支付
  const handlePay = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const order = await createOrder();

      if (!order.orderId || !order.qrCodeUrl) {
        message.error('创建订单失败');
        return;
      }

      onClose();

      openModal.modal({
        modalId: Modals.PAY_MODAL,
        content: () => (
          <PayQrModal
            orderId={order.orderId}
            qrCodeUrl={order.qrCodeUrl ?? ''}
            payType={PayType.wxpay}
            priceTitle={`${getCurrencySymbols()}${totalAmount.toFixed(2)}`}
            rePayOrder={() => createOrder()}
            onFinish={async () => {
              handlePaySuccess();
            }}
          />
        ),
      });
    } catch (error) {
      message.error((error as Error).message || '创建订单失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-[400px] p-6">
      <div className="mb-6 text-h3">购买兑换码</div>

      {/* 基础金额信息 */}
      <div className="mb-4 space-y-2 text-t2">
        <div className="flex justify-between">
          <span className="text-grey3">付费金额</span>
          <span>{accessFee} 元</span>
        </div>
        <div className="flex justify-between">
          <span className="text-grey3">兑换码单价（10%）</span>
          <span>{unitPrice} 元</span>
        </div>
      </div>

      {/* 数量选择 */}
      <div className="mb-4 flex items-center justify-between text-t2">
        <span className="text-grey3">购买数量</span>
        <div
          className="flex cursor-pointer items-center space-x-1 rounded border border-grey6 px-3 py-1.5"
          onClick={(e) => {
            openModal.dropdown({
              popcorn: e.currentTarget,
              content: ({ onCloseModal }) => (
                <ListView
                  className="next-modal w-[120px] py-1"
                  items={countOptions}
                  onItemClick={() => onCloseModal()}
                />
              ),
            });
          }}
        >
          <span>{count}</span>
          <Icon size="xxsmall" name="IcArrowDown01" />
        </div>
      </div>

      {/* 总金额 */}
      <div className="mb-4 flex items-center justify-between rounded bg-grey9 px-4 py-3">
        <span className="text-t2-medium">总金额</span>
        <span className="text-h3 text-active_color">{totalAmount} 元</span>
      </div>

      {/* 警告提示 */}
      <div className="mb-6 rounded bg-red/10 px-3 py-2 text-t4 text-red">
        ⚠️ 提示：兑换码与当前付费金额绑定。如果稍后修改付费金额，所有已购兑换码将会失效，无法恢复。
      </div>

      {/* 操作按钮 */}
      <div className="flex justify-end space-x-3">
        <Button colorType="secondary" onClick={onClose}>
          取消
        </Button>
        <Button colorType="active" onClick={handlePay} disable={loading}>
          {loading ? '处理中...' : '去支付'}
        </Button>
      </div>
    </div>
  );
};

/** 打开购买兑换码弹窗 */
export const useBuyWelfareCode = () => {
  const openModal = useOpenModal();

  return (props: Omit<BuyWelfareCodeModalProps, 'onClose'>) => {
    openModal.modal({
      content: ({ onCloseModal }) => <BuyWelfareCodeModal {...props} onClose={onCloseModal} />,
    });
  };
};
